import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from '@blaze-react/utils/lib/customHooks';
import Wrapper from '../Wrapper';
import ImageRender from './ImageRender';
import ImageDetails from './ImageDetails';
import getClassModifiers from '../../utils/get-class-modifiers';
import { getImageData } from '../../utils';
import { getDisplayCountData, shouldShowProperty, buildImageProperties } from '../../helpers';
import { IMAGE, HERO, IN_VIEW_CONFIG } from '../../constants';

const Image = ({
  url,
  modifier: _modifier,
  imageId,
  enableLightbox,
  name,
  toggleModal,
  handleSelectedImage,
  displayCount,
  lightboxImages,
  isHero,
  priority,
  type,
  areImageCreditsDisplayed,
  isCaptionDisplayed,
  images,
  imageData,
  imageUrl,
  elementTitle,
  credits,
  imageCredits,
  children,
  ...otherProps
}) => {
  const [displayLightbox, setDisplayLightbox] = useState(false);
  const [isIntersecting, outerRef] = useInView(IN_VIEW_CONFIG);

  const {
    caption,
    altText,
    hrefUrl,
    credits: imageDataCredits
  } = getImageData(otherProps, imageData);

  const href = url || hrefUrl;

  const { ImgWrapper, imgWrapperProps, lightboxImgProps } = buildImageProperties({
    href,
    name,
    enableLightbox,
    isHero
  });

  const modifier = _modifier || '';
  const modifiers = getClassModifiers(type, { modifier, ...otherProps });
  const wrapperClassName = isHero ? HERO : IMAGE;
  const toggleLightbox = () => setDisplayLightbox(!displayLightbox);

  const handleEnableLightbox = () => {
    if (!enableLightbox) return;
    toggleModal();
    handleSelectedImage(imageId);
  };

  const shouldDisplayCaption = shouldShowProperty({
    isDisplayed: isCaptionDisplayed,
    value: caption
  });

  // deprecated: imageCredits is deprecated. This code is to handle legacy data. check before changing/removing
  const creditsValue = credits || imageCredits || imageDataCredits;

  const shouldDisplayCredits = shouldShowProperty({
    isDisplayed: areImageCreditsDisplayed,
    value: creditsValue
  });
  const { shouldDisplayCount, countMessage } = getDisplayCountData(displayCount, images);
  const sizeKey = [IMAGE, isHero ? HERO : '', _modifier].filter(Boolean).join(':');
  const shouldDisplayImage = !!imageUrl && (priority || isIntersecting);

  return (
    <Wrapper ref={outerRef} className={wrapperClassName} modifiers={modifiers} {...otherProps}>
      {elementTitle && <div className="heading heading--section">{elementTitle}</div>}
      {shouldDisplayImage && (
        <ImgWrapper {...imgWrapperProps}>
          <ImageRender
            {...otherProps}
            alt={altText}
            imageUrl={imageUrl}
            handleEnableLightbox={handleEnableLightbox}
            toggleLightbox={toggleLightbox}
            displayLightbox={displayLightbox}
            caption={caption}
            enableLightbox={enableLightbox}
            lightboxImgProps={lightboxImgProps}
            name={name}
            displayCount={displayCount}
            lightboxImages={lightboxImages}
            isHero={isHero}
            priority={priority}
            sizeKey={sizeKey}
          />
          {children && <div className="image__children">{children}</div>}
        </ImgWrapper>
      )}
      {shouldDisplayCount && (
        <div
          className="image__button image__button--count"
          role="button"
          aria-label="Show more images"
          onClick={handleEnableLightbox}>
          <p>{countMessage}</p>
          <div className="arrow arrow--right" />
        </div>
      )}
      {(shouldDisplayCaption || shouldDisplayCredits) && (
        <ImageDetails
          caption={caption}
          credits={creditsValue}
          shouldDisplayCaption={shouldDisplayCaption}
          shouldDisplayCredits={shouldDisplayCredits}
        />
      )}
    </Wrapper>
  );
};

Image.propTypes = {
  url: PropTypes.string,
  name: PropTypes.string,
  modifier: PropTypes.string,
  imageId: PropTypes.string,
  enableLightbox: PropTypes.bool,
  toggleModal: PropTypes.func,
  handleSelectedImage: PropTypes.func,
  displayCount: PropTypes.bool,
  lightboxImages: PropTypes.array,
  images: PropTypes.array,
  isHero: PropTypes.bool,
  priority: PropTypes.bool,
  type: PropTypes.string,
  areImageCreditsDisplayed: PropTypes.bool,
  isCaptionDisplayed: PropTypes.bool,
  imageData: PropTypes.object,
  imageUrl: PropTypes.string,
  credits: PropTypes.string,
  imageCredits: PropTypes.string,
  elementTitle: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

Image.defaultProps = {
  modifier: '',
  url: null,
  name: '',
  credits: '',
  imageCredits: '',
  enableLightbox: false,
  imageId: '',
  handleSelectedImage: () => {},
  toggleModal: () => {},
  displayCount: false,
  lightboxImages: [],
  images: [],
  isHero: false,
  priority: false,
  type: '',
  areImageCreditsDisplayed: false,
  isCaptionDisplayed: true,
  imageData: {},
  imageUrl: '',
  elementTitle: '',
  children: null
};

export default Image;
